import { taskCarriesModifierTag } from '@kaiizen/kai-presenters';
import { ApiOrgContractor, type OverallTaskStatus } from '@kaiizen/kai-pro-api';
import {
  getModifierFilterTag,
  isModifierFilterKey,
  isValidModifierFilterKey,
  MODIFIER_FILTER_PREFIX,
  type ModifierFilterKey,
  toCanonicalModifierFilterKey,
  toModifierFilterKey,
  toModifierFilterKeys,
} from '@kaiizen/kai-pro-api-react-state';
import {
  getTaskLineItemsInScope,
  isTaskScoped,
  isTaskShownOnScopeReport,
  type ScanGroupOptimized,
  type ScanLineItemOptimized,
  type ScanTaskOptimized,
} from '@kaiizen/kai-scan-template-system';

import { isScopeNavigatorTask } from './scopeNavigatorPartPresentersLib';

/**
 * Workspace attention filters. Most evaluate from scan-task data alone;
 * `unassignedContractors` additionally needs a contractor resolver supplied via
 * {@link ScopeAttentionFilterContext}. (Out of stock / edit request / change
 * order are added in the phase that wires their data source.)
 */
export type ScopeAttentionFilterType =
  | 'missingProduct'
  | 'unassignedContractors'
  | 'editRequests'
  | 'changeOrders'
  | 'tenantResp'
  | 'deferredTasks'
  | 'outOfStock';

/**
 * Canonical order — used for stable URL serialization and the dropdown.
 *
 * `tenantResp` and `deferredTasks` are no longer offered as rows: the Modifiers group is built
 * from the org's own settings, and `toCanonicalScopeFilterKey` rewrites both to their tag keys.
 * They stay here so a link saved before KAIDEV-4206 still parses and still evaluates. Nothing
 * should put them back in the dropdown — a user would get two rows for one filter. They can go
 * once saved links of that vintage no longer matter.
 */
export const SCOPE_ATTENTION_FILTER_TYPES: readonly ScopeAttentionFilterType[] = [
  'missingProduct',
  'unassignedContractors',
  'editRequests',
  'changeOrders',
  'tenantResp',
  'deferredTasks',
  'outOfStock',
];

/**
 * The modifier filter vocabulary now lives in `@kaiizen/kai-pro-api-react-state` so kaiPro
 * addresses an org's modifiers the same way this app does (KAIDEV-4206). Re-exported here because
 * every caller in Renovator reaches it through this module, and because a modifier filter is only
 * meaningful next to the attention filters it sits beside in the dropdown.
 *
 * What stayed local is the legacy alias map below: kaiPro's pre-modifier filters were named
 * `deferred` and `tenantResponsibility`, these were named `deferredTasks` and `tenantResp`, and
 * only the rewrite rule is common to both.
 */
export {
  getModifierFilterTag,
  isModifierFilterKey,
  MODIFIER_FILTER_PREFIX,
  type ModifierFilterKey,
  toModifierFilterKey,
  toModifierFilterKeys,
};

/** Either kind of filter the scope dropdown can hold. */
export type ScopeFilterKey = ScopeAttentionFilterType | ModifierFilterKey;

/**
 * The two modifier filters that predate org-configured ones, and the tags they stand for.
 *
 * A link saved before this change carries `tenantResp` or `deferredTasks`. Both still match, but
 * the dropdown's rows are keyed by tag now, so an untranslated key would filter correctly while
 * showing nothing as selected.
 */
const MODIFIER_TAG_BY_LEGACY_FILTER: Partial<Record<ScopeAttentionFilterType, string>> = {
  tenantResp: 'tenantResponsibility',
  deferredTasks: 'deferred',
};

export const toCanonicalScopeFilterKey = (key: ScopeFilterKey): ScopeFilterKey => toCanonicalModifierFilterKey(key, MODIFIER_TAG_BY_LEGACY_FILTER);

export const isScopeFilterKey = (value: string): value is ScopeFilterKey => isScopeAttentionFilterType(value) || isValidModifierFilterKey(value);

export const DEFAULT_NEEDS_ATTENTION_TITLE = 'Needs attention';

/** A task shown on the scope report (has values, photos, or notes) but never priced —
 *  the "documentation task" exception the unassigned-contractor rule excludes. */
export const isDocumentationTask = (task: ScanTaskOptimized): boolean => isTaskShownOnScopeReport(task) && !isTaskScoped(task);

/**
 * Evaluation inputs that a few filters need beyond the task itself. Threaded
 * from the consuming surface (via `ScopeAttentionFilterProvider`); an unset
 * resolver means its filter simply can't match.
 */
/**
 * What the filters need in order to evaluate, carried as the values
 * ScanEditingContext already holds rather than as adapter closures over them.
 * An absent value means its filter simply can't match.
 */
export type ScopeAttentionFilterContext = {
  /**
   * The scan-editing context's own resolver, passed through unchanged — not a
   * `getContractorName` wrapper around it. Whoever needs a contractor reads this
   * from context and hands it over as-is.
   */
  getContractorForTask?: (task: ScanTaskOptimized) => ApiOrgContractor | undefined;
  /**
   * Tasks with an open edit request — an `editRequest` note with no subsequent
   * `editResolution`. Comes straight from `editRequestTaskIds`.
   */
  editRequestTaskIds?: ReadonlySet<string>;
  /**
   * Tasks differing from their change-order baseline. Comes straight from
   * `changedScopedTaskIds`; nobody should recompute it.
   */
  changeOrderTaskIds?: ReadonlySet<string>;
  overallTaskStatusByTaskId?: Readonly<Record<string, OverallTaskStatus>>;
  taskStatus?: OverallTaskStatus;
};

const EMPTY_CONTEXT: ScopeAttentionFilterContext = {};

const SCOPE_ATTENTION_FILTER_TYPE_SET = new Set<string>(SCOPE_ATTENTION_FILTER_TYPES);

export const isScopeAttentionFilterType = (value: string): value is ScopeAttentionFilterType => SCOPE_ATTENTION_FILTER_TYPE_SET.has(value);

const lineItemHasMissingProduct = (lineItem: ScanLineItemOptimized): boolean => (lineItem.template?.skuIdOptions?.length ?? 0) > 0 && lineItem.skuId == null;

/** A scoped task counts as unassigned when it has no contractor name (or "Unassigned") —
 *  except documentation tasks, which are never expected to carry a contractor. */
const taskHasUnassignedContractor = (
  task: ScanTaskOptimized,
  context: ScopeAttentionFilterContext,
): boolean => {
  if (isDocumentationTask(task)) return false;
  if (!context.getContractorForTask) return false;
  const name = context.getContractorForTask(task)?.name?.trim();
  return !name || name.toLowerCase() === 'unassigned';
};

export const taskMatchesScopeAttentionFilter = (
  task: ScanTaskOptimized,
  filter: ScopeAttentionFilterType,
  context: ScopeAttentionFilterContext = EMPTY_CONTEXT,
): boolean => {
  switch (filter) {
    case 'missingProduct':
      return getTaskLineItemsInScope(task).some(lineItemHasMissingProduct);
    case 'unassignedContractors':
      return isScopeNavigatorTask(task) && taskHasUnassignedContractor(task, context);
    case 'editRequests':
      return isScopeNavigatorTask(task) && (context.editRequestTaskIds?.has(task.id) ?? false);
    case 'changeOrders':
      return isScopeNavigatorTask(task) && (context.changeOrderTaskIds?.has(task.id) ?? false);
    case 'deferredTasks':
    case 'tenantResp':
      return taskCarriesModifierTag(task, MODIFIER_TAG_BY_LEGACY_FILTER[filter]!);
    case 'outOfStock':
      // No live stock data source exists yet — see kai-wiki/work/specs/2026-08-26-attention-flag-design.md.
      return false;
  }
};

/**
 * Matches either kind of filter. A modifier filter asks only whether the task carries that tag —
 * the org decides what the tag means, so there is nothing here to interpret.
 */
export const taskMatchesScopeFilter = (
  task: ScanTaskOptimized,
  filter: ScopeFilterKey,
  context: ScopeAttentionFilterContext = EMPTY_CONTEXT,
): boolean =>
  isModifierFilterKey(filter)
    ? taskCarriesModifierTag(task, getModifierFilterTag(filter))
    : taskMatchesScopeAttentionFilter(task, filter, context);

export const taskMatchesAnyScopeAttentionFilter = (
  task: ScanTaskOptimized,
  activeFilters: ReadonlySet<ScopeFilterKey>,
  context: ScopeAttentionFilterContext = EMPTY_CONTEXT,
): boolean => {
  if (activeFilters.size === 0) return true;
  for (const filter of activeFilters) {
    if (taskMatchesScopeFilter(task, filter, context)) return true;
  }
  return false;
};

export const filterScopeNavigatorTasks = (
  tasks: readonly ScanTaskOptimized[],
  activeFilters: ReadonlySet<ScopeFilterKey>,
  context: ScopeAttentionFilterContext = EMPTY_CONTEXT,
): ScanTaskOptimized[] => {
  const visibleTasks = tasks.filter(isScopeNavigatorTask);
  return visibleTasks.filter(task => taskMatchesScopeNavigatorSelection(task, activeFilters, context));
};

const getScopeNavigatorTasks = (groups: readonly ScanGroupOptimized[]): ScanTaskOptimized[] =>
  groups.flatMap(group => (group.tasks ?? []).filter(isScopeNavigatorTask));

const taskMatchesSelectedStatus = (task: ScanTaskOptimized, context: ScopeAttentionFilterContext): boolean =>
  context.taskStatus == null || (context.overallTaskStatusByTaskId?.[task.id] ?? 'draft') === context.taskStatus;

const taskMatchesScopeNavigatorSelection = (
  task: ScanTaskOptimized,
  activeFilters: ReadonlySet<ScopeFilterKey>,
  context: ScopeAttentionFilterContext,
): boolean => taskMatchesSelectedStatus(task, context) && taskMatchesAnyScopeAttentionFilter(task, activeFilters, context);

/** Counts for the "Showing {filteredCount} of {totalCount}" footer. */
export const countScopeNavigatorFilterTotals = (
  groups: readonly ScanGroupOptimized[],
  activeFilters: ReadonlySet<ScopeFilterKey>,
  context: ScopeAttentionFilterContext = EMPTY_CONTEXT,
): { filteredCount: number; totalCount: number } => {
  const tasks = getScopeNavigatorTasks(groups);
  const filteredCount = tasks.filter(task => taskMatchesScopeNavigatorSelection(task, activeFilters, context)).length;
  return { filteredCount, totalCount: tasks.length };
};

/** Per-filter task counts (the number beside each dropdown row). */
export const countTasksByScopeAttentionFilter = (
  groups: readonly ScanGroupOptimized[],
  context: ScopeAttentionFilterContext = EMPTY_CONTEXT,
  /** Modifier filters the org has configured; their counts are wanted alongside the fixed ones. */
  modifierFilters: readonly ModifierFilterKey[] = [],
): Record<string, number> => {
  const tasks = getScopeNavigatorTasks(groups);
  const filters: ScopeFilterKey[] = [...SCOPE_ATTENTION_FILTER_TYPES, ...modifierFilters];
  const counts = Object.fromEntries(filters.map(filter => [filter, 0])) as Record<string, number>;
  for (const task of tasks) {
    for (const filter of filters) {
      if (taskMatchesSelectedStatus(task, context) && taskMatchesScopeFilter(task, filter, context)) counts[filter] += 1;
    }
  }
  return counts;
};

/**
 * Whether each filter type also counts toward the single "does this task need
 * attention" flag icon — `missingProduct`/`editRequests` are filter-only, not
 * flag reasons. A Record, not a bare array, so TypeScript forces a decision
 * here whenever a new ScopeAttentionFilterType is added.
 */
const IS_ATTENTION_FLAG_TYPE: Record<ScopeAttentionFilterType, boolean> = {
  missingProduct: false,
  unassignedContractors: true,
  editRequests: false,
  changeOrders: true,
  tenantResp: true,
  deferredTasks: true,
  outOfStock: true,
};

export const TASK_ATTENTION_FLAG_TYPES: readonly ScopeAttentionFilterType[] = SCOPE_ATTENTION_FILTER_TYPES.filter(type => IS_ATTENTION_FLAG_TYPE[type]);

const TASK_ATTENTION_FLAG_TYPE_SET: ReadonlySet<ScopeAttentionFilterType> = new Set(TASK_ATTENTION_FLAG_TYPES);

/** The single per-task boolean the flag icon renders from, everywhere it appears. */
export const taskNeedsAttentionFlag = (
  task: ScanTaskOptimized,
  context: ScopeAttentionFilterContext = EMPTY_CONTEXT,
): boolean => taskMatchesAnyScopeAttentionFilter(task, TASK_ATTENTION_FLAG_TYPE_SET, context);
